import { useEffect, useState, useRef } from 'react';
import { formatDate } from '../../../utilities/dateHelpers';
import { useAppDispatch, useAppSelector } from '../../../hooks/storeHooks';
import Loader from '../../common/Loader/Loader';
import LidarScanMap from '../../rod-lift-analysis/LidarScan';
import { ReactComponent as NoData } from '../../../images/no-data.svg';
import './CustomViewCardViewer.scss';
import InputDropdown from '../../../components/common/InputDropdown';
import InputDropdownMenu, { DropdownOption } from '../../../components/common/InputDropdownMenu';
import { MultiSelectToggleDropdown } from '../../../components/common/MultiSelectToggleDropdown';
import { fetchCardViewerDataAsync } from './CardViewerSlice';
import { buildCardDataForLidarScan } from './CardViewerHelper';
import { CardType } from '../../../constants/card-type.constant';
import { Link } from 'react-router-dom';
import SortDropdown, { SortOption } from '../../../components/common/SortDropdown/SortDropdown';

const sortOptions: SortOption[] = [
  { label: 'Date', value: 'lastCardDateTime' },
  { label: 'Well Name', value: 'wellName' },
];

const cardSelectorOptions: DropdownOption[] = [
  { label: 'Last card', value: 'last-card' },
  { label: 'Shutdown', value: 'shutdown' },
  { label: 'Startup', value: 'startup' },
];

// Multi-overlay options for MultiSelectToggleDropdown
const overlayOptions = [
  { label: 'Surface card', value: 'surface', id: 1 },
  { label: 'POC downhole', value: 'pocDownhole', id: 2 },
  { label: 'XDiag downhole', value: 'xdiagDownhole', id: 3 },
  { label: 'Load limits', value: 'loadLimits', id: 9 },
  { label: 'Permissible loads', value: 'permissibleLoads', id: 5 },
  { label: 'Hide unavailable wells', value: 'hideUnavailableWells', id: 6 },
];

interface CustomViewCardViewerProps {
  groupName: string;
}

const CustomViewCardViewer = ({ groupName }: CustomViewCardViewerProps) => {
  const dispatch = useAppDispatch();
  const cardViewerStore = useAppSelector((state) => state.cardViewerSlice);
  const isLoadingCards = cardViewerStore.isLoading;

  const cardSelectorToType: Record<string, CardType> = {
    'last-card': CardType.Current,
    shutdown: CardType.Shutdown,
    startup: CardType.Startup,
  };

  const [selected, setSelected] = useState<string | number | null>(null);
  const [direction, setDirection] = useState<'asc' | 'desc' | ''>('');
  const [cardData, setCardData] = useState(cardViewerStore.values);

  const [selectedOverlays, setSelectedOverlays] = useState<string[]>(['surface']);
  const hideUnavailableWells = selectedOverlays.includes('hideUnavailableWells');
  const [isCardSelectorOpen, setIsCardSelectorOpen] = useState(false);
  const [selectedCardSelector, setSelectedCardSelector] = useState(cardSelectorOptions[0].value);
  const selectedCardLabel = cardSelectorOptions.find((opt) => opt.value === selectedCardSelector)?.label || '';
  const [pageNumber, setPageNumber] = useState(1);
  const [pagesLoaded, setPagesLoaded] = useState(0);
  const pageSize = 12;

  const cardSelectorRef = useRef<HTMLDivElement>(null);
  const gridRef = useRef<HTMLDivElement>(null);
  const isFetchingRef = useRef(false);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    if (!groupName) return;

    isFetchingRef.current = true;

    dispatch(
      fetchCardViewerDataAsync({
        groupName,
        pageNumber,
        pageSize,
        cardType: cardSelectorToType[selectedCardSelector],
      }),
    ).then((res: any) => {
      const { pageNumber: currentPage } = res.payload;
      setPagesLoaded((prev) => Math.max(prev, currentPage));

      isFetchingRef.current = false;
    });
  }, [groupName, pageNumber, selectedCardSelector, dispatch]);

  useEffect(() => {
    setPageNumber(1);
    setPagesLoaded(0);
    setCardData([]);
  }, [groupName, selectedCardSelector]);

  useEffect(() => {
    const totalPages = cardViewerStore.totalPages || 0;

    const handleScroll = () => {
      if (isLoadingCards || isFetchingRef.current || pagesLoaded >= totalPages) return;

      const el = gridRef.current?.parentElement;

      const scrollTop = el ? el.scrollTop : window.scrollY;
      const clientHeight = el ? el.clientHeight : window.innerHeight;
      const scrollHeight = el ? el.scrollHeight : document.documentElement.scrollHeight;

      if (scrollTop + clientHeight >= scrollHeight - 100) {
        isFetchingRef.current = true;
        setPageNumber((prev) => prev + 1);
      }
    };

    const onScroll = () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      timeoutRef.current = setTimeout(handleScroll, 100);
    };

    const scrollTarget = gridRef.current?.parentElement || window;
    scrollTarget.addEventListener('scroll', onScroll);

    return () => {
      scrollTarget.removeEventListener('scroll', onScroll);
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, [isLoadingCards, pagesLoaded, cardViewerStore.totalPages]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (cardSelectorRef.current && !cardSelectorRef.current.contains(event.target as Node)) {
        setIsCardSelectorOpen(false);
      }
    };

    if (isCardSelectorOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isCardSelectorOpen]);

  useEffect(() => {
    if (!direction || !selected) {
      setCardData(cardViewerStore.values);
      return;
    }

    const sortedData = [...(cardViewerStore.values || [])].sort((a: any, b: any) => {
      if (selected === 'wellName') {
        return direction === 'asc'
          ? a.wellName.trim().localeCompare(b.wellName.trim())
          : b.wellName.trim().localeCompare(a.wellName.trim());
      }

      if (selected === 'lastCardDateTime') {
        const dateA = new Date(a.lastCardDateTime).getTime();
        const dateB = new Date(b.lastCardDateTime).getTime();
        return direction === 'asc' ? dateA - dateB : dateB - dateA;
      }
      return 0;
    });
    setCardData(sortedData);
  }, [direction, selected, cardViewerStore.values]);

  const handleCardSelectorChange = (value: string) => {
    setSelectedCardSelector(value);
  };

  return (
    <div className='custom-view-card-grid' ref={gridRef}>
      <div className='flex justify-between items-center mb-4 mx-6'>
        <div className='card-view-options'>
          <div className='mb-4 flex items-center pr-[20px]'>
            <div ref={cardSelectorRef} style={{ position: 'relative', width: '280px' }}>
              <InputDropdown
                value={selectedCardLabel}
                onClick={() => setIsCardSelectorOpen(!isCardSelectorOpen)}
                isOpen={isCardSelectorOpen}
              />
              <div>
                <InputDropdownMenu
                  title='Card type'
                  options={cardSelectorOptions}
                  selectedValue={selectedCardSelector}
                  onChange={handleCardSelectorChange}
                  isOpen={isCardSelectorOpen}
                  onClose={() => setIsCardSelectorOpen(false)}
                  disableClickOutside={true}
                />
              </div>
            </div>
            <div className='ml-6 flex items-center'>
              <MultiSelectToggleDropdown
                options={overlayOptions}
                selected={selectedOverlays}
                onChange={setSelectedOverlays}
                placeholder='Card overlay'
              />
            </div>
          </div>
        </div>
        <div>
          <SortDropdown
            options={sortOptions}
            selectedOption={selected}
            direction={direction}
            onOptionChange={(option) => setSelected(option)}
            onDirectionChange={(dir) => setDirection(dir)}
          />
        </div>
      </div>
      <div>
        {isLoadingCards && pageNumber === 1 ? (
          <div className='custom-view-card-viewer-loader'>
            <Loader />
          </div>
        ) : (
          <>
            <div className='custom-view-card-viewer-grid'>
              {(cardData ?? [])
                .filter((asset) => !(hideUnavailableWells && !asset.hasCardData))
                .map((asset) => (
                  <div className='custom-view-card-viewer' key={asset.assetId}>
                    <div className='custom-view-card-viewer-header-content'>
                      <div className='custom-view-card-viewer-header'>
                        <Link
                          to={`/asset/status/${asset.assetId}/${groupName}`}
                          className='custom-view-card-viewer-header-link'
                          target='_blank'
                          rel='noopener noreferrer'
                          title={asset.wellName}
                          aria-label={asset.wellName}
                        >
                          {asset.wellName}
                        </Link>
                      </div>
                      <div className='custom-view-card-viewer-date'>
                        <span
                          className='custom-view-card-viewer-date-label'
                          title={`${selectedCardLabel}: ${asset.lastCardDateFormatted}`}
                          aria-label={`${selectedCardLabel}: ${asset.lastCardDateFormatted}`}
                        >
                          {selectedCardLabel}: {formatDate(asset.lastCardDateFormatted)}
                        </span>
                      </div>
                    </div>
                    <div className='card-header-divider' />
                    <div className='flex flex-row flex-grow flex-shrink'>
                      {asset.hasCardData && selectedOverlays.length > 0 ? (
                        <LidarScanMap
                          selectedCardTrends={selectedOverlays.map((overlay) => {
                            const found = overlayOptions.find((opt) => opt.value === overlay);
                            return found ? { id: found.id, label: found.label } : { id: 0, label: overlay };
                          })}
                          cards={[
                            {
                              cardDate: asset.lastCardDateFormatted,
                              cardTrends: buildCardDataForLidarScan(
                                asset,
                                selectedOverlays.map((overlay) => {
                                  const found = overlayOptions.find((opt) => opt.value === overlay);
                                  return found ? { id: found.id, label: found.label } : { id: 0, label: overlay };
                                }),
                              )
                                .flatMap((card) => card.cardTrends)
                                .map((trend) => ({
                                  ...trend,
                                  name: trend.label,
                                })),
                              color: { r: 239, g: 68, b: 68 },
                            },
                          ]}
                          chartId={`lidarScanMapDiv-${asset.assetId}`}
                          height='100%'
                          disableZoom={true}
                        />
                      ) : (
                        <div className='flex flex-col items-center justify-center h-full w-full'>
                          <NoData />
                          <div className='custom-card-no-data-text'>Card not available. Please check later.</div>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
            </div>
            {isLoadingCards && pageNumber > 1 && <Loader />}
          </>
        )}
      </div>
    </div>
  );
};

export default CustomViewCardViewer;
